import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import { toast } from 'sonner';

export function useAggregation() {
  const queryClient = useQueryClient();
  const [activeRunId, setActiveRunId] = useState<number | null>(null);

  const triggerMutation = useMutation({
    mutationFn: () => analyticsApi.triggerAggregation(),
    onSuccess: (data) => {
      setActiveRunId(data.runId);
      toast.info('Aggregation started');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const runQuery = useQuery({
    queryKey: queryKeys.analytics.run(activeRunId!),
    queryFn: () => analyticsApi.getRun(activeRunId!),
    enabled: activeRunId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'running') return 2000;
      return false;
    },
  });

  useEffect(() => {
    if (runQuery.data && activeRunId !== null) {
      if (runQuery.data.status === 'completed') {
        toast.success(`Aggregation completed in ${runQuery.data.durationMs}ms`);
        setActiveRunId(null);
        queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      } else if (runQuery.data.status === 'failed') {
        toast.error(`Aggregation failed: ${runQuery.data.errorMessage || 'Unknown error'}`);
        setActiveRunId(null);
      }
    }
  }, [runQuery.data, activeRunId, queryClient]);

  const trigger = useCallback(() => {
    triggerMutation.mutate();
  }, [triggerMutation]);

  return {
    trigger,
    isRunning: activeRunId !== null,
    isPending: triggerMutation.isPending,
    currentRun: runQuery.data ?? null,
  };
}
